import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Progress,
  message,
  Tooltip,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  PoweroffOutlined,
  PlaySquareOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { AvatarCharacter, ProgressInfo } from '@/services/api/character';
import { characterApi } from '@/services/api/character';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

const CharacterList: React.FC = () => {
  const navigate = useNavigate();
  const [characters, setCharacters] = useState<AvatarCharacter[]>([]);
  const [loading, setLoading] = useState(false);
  const [progressMap, setProgressMap] = useState<Record<string, ProgressInfo>>({});
  const [sdStatus, setSdStatus] = useState<{
    status: 'running' | 'stopped';
    progress: number;
    eta: number;
    currentModel?: string;
  } | null>(null);

  useEffect(() => {
    loadCharacters();
    loadSdStatus();
    // 生成中的任务轮询更新
    const interval = setInterval(() => {
      loadProgressForGenerating();
      loadSdStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const loadSdStatus = async () => {
    try {
      const res = await characterApi.getSdStatus();
      if (res.code === 200) {
        setSdStatus(res.data);
      }
    } catch (error: any) {
      // 403 错误表示未登录，静默忽略
      if (error.response?.status !== 403) {
        console.error('查询 SD 状态失败:', error);
      }
    }
  };

  const loadCharacters = async () => {
    setLoading(true);
    try {
      const res = await characterApi.getList();
      if (res.code === 200) {
        setCharacters(res.data);
      }
    } catch (error: any) {
      // 403 错误表示未登录，静默忽略
      if (error.response?.status !== 403) {
        message.error('加载列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProgressForGenerating = async () => {
    const generatingChars = characters.filter(
      (c) => c.status === 'generating' || c.status === 'pending'
    );
    if (generatingChars.length === 0) return;

    for (const char of generatingChars) {
      try {
        const res = await characterApi.getProgress(char.id);
        if (res.code === 200 && res.data) {
          setProgressMap((prev) => ({ ...prev, [char.id]: res.data }));

          // 如果状态变化，刷新列表
          if (res.data.status !== char.status) {
            loadCharacters();
          }
        }
      } catch (error: any) {
        // 403 错误表示未登录，静默忽略
        if (error.response?.status !== 403) {
          console.error('查询进度失败:', error);
        }
      }
    }
  };

  // 删除角色
  const handleDelete = (char: AvatarCharacter) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除角色 "${char.name}" 吗？这将删除所有已生成的图片文件。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await characterApi.delete(char.characterId);
          if (res.code === 200) {
            message.success('删除成功');
            loadCharacters();
          } else {
            message.error(res.message || '删除失败');
          }
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 重新生成
  const handleRetry = async (char: AvatarCharacter) => {
    try {
      const res = await characterApi.retry(char.characterId);
      if (res.code === 200) {
        message.success('重新生成任务已创建');
        loadCharacters();
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 预览角色
  const handlePreview = (char: AvatarCharacter) => {
    console.log('[Preview] 点击预览:', char);
    console.log('[Preview] characterId:', char.characterId);
    if (!char.characterId) {
      message.warning('角色 ID 不存在');
      return;
    }
    navigate(`/character/preview/${char.characterId}`);
  };

  // 状态渲染
  const renderStatus = (char: AvatarCharacter) => {
    const progress = progressMap[char.id];
    const status = progress?.status || char.status;

    if (status === 'completed') {
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          已完成
        </Tag>
      );
    }
    if (status === 'failed') {
      return (
        <Tooltip title={progress?.errorMessage || char.errorMessage}>
          <Tag icon={<CloseCircleOutlined />} color="error">
            失败
          </Tag>
        </Tooltip>
      );
    }
    if (status === 'running' || status === 'generating') {
      return (
        <Tag icon={<LoadingOutlined spin />} color="processing">
          生成中 {progress?.progress || char.progress}%
        </Tag>
      );
    }
    return <Tag color="default">等待中</Tag>;
  };

  const columns = [
    {
      title: '角色 ID',
      dataIndex: 'characterId',
      key: 'characterId',
      width: 150,
      render: (text: string) => <code>{text}</code>,
    },
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '外貌描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 300,
    },
    {
      title: '分辨率',
      dataIndex: 'resolution',
      key: 'resolution',
      width: 100,
      render: (res: string) => {
        const map: Record<string, string> = {
          quick: '快速 512x768',
          standard: '标准 512x910',
          hd: '高清 1024x1536',
        };
        return map[res] || res;
      },
    },
    {
      title: '状态',
      key: 'status',
      width: 120,
      render: (_: unknown, record: AvatarCharacter) => renderStatus(record),
    },
    {
      title: '进度',
      key: 'progress',
      width: 150,
      render: (_: unknown, record: AvatarCharacter) => {
        const progress = progressMap[record.id];
        const pct = progress?.progress || record.progress || 0;
        return (
          <Progress
            percent={pct}
            size="small"
            status={
              progress?.status === 'completed'
                ? 'success'
                : progress?.status === 'failed'
                ? 'exception'
                : 'active'
            }
          />
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, record: AvatarCharacter) => (
        <Space size="small">
          <Tooltip title="预览">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
            />
          </Tooltip>
          {(record.status === 'failed' || record.status === 'completed') && (
            <Tooltip title="重新生成">
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRetry(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="删除">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>电子人列表</span>
          </Space>
        }
        extra={
          <Space>
            {/* SD WebUI 状态 */}
            {sdStatus && (
              <Space size="large">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag icon={sdStatus.status === 'running' ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={sdStatus.status === 'running' ? 'success' : 'error'}>
                    SD WebUI: {sdStatus.status === 'running' ? '运行中' : '已停止'}
                  </Tag>
                  {sdStatus.status === 'running' && sdStatus.currentModel && (
                    <span style={{ fontSize: 12, color: '#666' }}>
                      模型：{sdStatus.currentModel.substring(0, 20)}...
                    </span>
                  )}
                  {sdStatus.status === 'running' ? (
                    <Button
                      size="small"
                      icon={<PlaySquareOutlined />}
                      onClick={() => window.open('http://localhost:7860', '_blank')}
                    >
                      打开 SD WebUI
                    </Button>
                  ) : (
                    <Tooltip title="请在 D:\\SD_Forge 启动 SD WebUI Forge">
                      <Button
                        size="small"
                        danger
                        icon={<PoweroffOutlined />}
                        disabled
                      >
                        已停止
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </Space>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/character/generate')}
            >
              生成新角色
            </Button>
          </Space>
        }
        variant="bordered"
      >
        <Table
          dataSource={characters}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            showQuickJumper: true
          }}
          scroll={{ x: 1200, y: 'calc(100vh - 280px)' }}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default CharacterList;
